import { type ComponentType, type SVGProps } from "react";
import type { KnownVehicleEventType } from "@/lib/notifications/vehicle-events";

/**
 * The vehicle event icons from `assets/vehicle-event-icons/` — one per event type, 24×24,
 * `currentColor` stroke. The bodies below are copied verbatim from that folder's `sprite.svg`;
 * edit the assets and re-copy rather than touching the paths here.
 */
export const VEHICLE_EVENT_ICON_BODIES: Record<KnownVehicleEventType, string> = {
  parking_enter:
    '<path d="M14 12V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h7a2 2 0 0 0 2-2"/><path d="M7 11V6h2a1.5 1.5 0 0 1 0 3H7"/><path d="M21 16H10m4-4-4 4 4 4"/>',
  parking_exit:
    '<path d="M14 12V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h7a2 2 0 0 0 2-2"/><path d="M7 11V6h2a1.5 1.5 0 0 1 0 3H7"/><path d="M10 16h11m-4-4 4 4-4 4"/>',
  go_zone_enter:
    '<path d="M14 12V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h7a2 2 0 0 0 2-2"/><path d="m6 8 2 2 3-4"/><path d="M21 16H10m4-4-4 4 4 4"/>',
  go_zone_exit:
    '<path d="M14 12V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h7a2 2 0 0 0 2-2"/><path d="m6 8 2 2 3-4"/><path d="M10 16h11m-4-4 4 4-4 4"/>',
  nogo_zone_enter:
    '<path d="M14 12V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h7a2 2 0 0 0 2-2"/><circle cx="8.5" cy="8" r="2.5"/><path d="m6.7 6.3 3.6 3.4"/><path d="M21 16H10m4-4-4 4 4 4"/>',
  nogo_zone_exit:
    '<path d="M14 12V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h7a2 2 0 0 0 2-2"/><circle cx="8.5" cy="8" r="2.5"/><path d="m6.7 6.3 3.6 3.4"/><path d="M10 16h11m-4-4 4 4-4 4"/>',
  driver_identified:
    '<circle cx="8" cy="7" r="3"/><path d="M2.5 20v-2a5.5 5.5 0 0 1 11 0v2"/><path d="m15 11 2 2 4-5"/>',
  driver_removed:
    '<circle cx="8" cy="7" r="3"/><path d="M2.5 20v-2a5.5 5.5 0 0 1 11 0v2"/><path d="M16 11h6"/>',
  driver_changed:
    '<circle cx="8" cy="7" r="3"/><path d="M2.5 20v-2a5.5 5.5 0 0 1 11 0v2"/><path d="M15 7h7m-2-2 2 2-2 2M22 15h-7m2-2-2 2 2 2"/>',
  started:
    '<circle cx="12" cy="12" r="9"/><path d="m10 8 6 4-6 4Z"/>',
  stopped:
    '<circle cx="12" cy="12" r="9"/><rect x="9" y="9" width="6" height="6" rx=".5"/>',
  speeding_start:
    '<path d="M4 17a9 9 0 1 1 16 0M5 16l1.5-1M5 9l1.5 1M12 4v2M19 9l-1.5 1M12 13l3-4"/><circle cx="12" cy="13" r="1"/><path d="M12 18v4m-2-2 2-2 2 2"/>',
  speeding_end:
    '<path d="M4 17a9 9 0 1 1 16 0M5 16l1.5-1M5 9l1.5 1M12 4v2M19 9l-1.5 1M12 13l3-4"/><circle cx="12" cy="13" r="1"/><path d="m9 20 2 2 4-4"/>',
  street_speeding_start:
    '<path d="M7 3 3 21M17 3l1.7 8M12 3v3m0 4v3m0 5v3"/><path d="M19 21v-7m-3 3 3-3 3 3"/>',
  street_speeding_end:
    '<path d="M7 3 3 21M17 3l1.7 8M12 3v3m0 4v3m0 5v3"/><path d="m16 18 2 2 4-5"/>',
  refuel:
    '<path d="M10 3C8 6 3 11 3 15a7 7 0 0 0 13 3.6M16.6 12C15.2 8.7 11.4 5 10 3M6 15a4 4 0 0 0 3 3.8"/><path d="M19 13v8m-4-4h8"/>',
  missing_fuel:
    '<path d="M10 3C8 6 3 11 3 15a7 7 0 0 0 13 3.6M16.6 12C15.2 8.7 11.4 5 10 3M6 15a4 4 0 0 0 3 3.8"/><path d="M15 17h8"/>',
  overheat:
    '<path d="M8 14.5V5a3 3 0 0 1 6 0v9.5a5 5 0 1 1-6 0Z M11 9v9M17 5h3m-3 4h2m-2 4h3"/><circle cx="11" cy="18" r="1"/>',
};

const DEFAULT_STROKE_WIDTH = 1.75;

/** The props every event icon is rendered with — satisfied by lucide icons and by the ones below. */
export type EventIconProps = Pick<
  SVGProps<SVGSVGElement>,
  "className" | "width" | "height" | "strokeWidth" | "aria-hidden"
>;
export type EventIconComponent = ComponentType<EventIconProps>;

function createEventIcon(type: KnownVehicleEventType): EventIconComponent {
  const markup = { __html: VEHICLE_EVENT_ICON_BODIES[type] };
  function EventIcon({ width = 24, height = 24, strokeWidth = DEFAULT_STROKE_WIDTH, ...rest }: EventIconProps) {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width={width}
        height={height}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        {...rest}
        // Static markup from the module above, never user data.
        dangerouslySetInnerHTML={markup}
      />
    );
  }
  EventIcon.displayName = `EventIcon(${type})`;
  return EventIcon;
}

export const ParkingEnterIcon = createEventIcon("parking_enter");
export const ParkingExitIcon = createEventIcon("parking_exit");
export const GoZoneEnterIcon = createEventIcon("go_zone_enter");
export const GoZoneExitIcon = createEventIcon("go_zone_exit");
export const NogoZoneEnterIcon = createEventIcon("nogo_zone_enter");
export const NogoZoneExitIcon = createEventIcon("nogo_zone_exit");
export const DriverIdentifiedIcon = createEventIcon("driver_identified");
export const DriverRemovedIcon = createEventIcon("driver_removed");
export const DriverChangedIcon = createEventIcon("driver_changed");
export const StartedIcon = createEventIcon("started");
export const StoppedIcon = createEventIcon("stopped");
export const SpeedingStartIcon = createEventIcon("speeding_start");
export const SpeedingEndIcon = createEventIcon("speeding_end");
export const StreetSpeedingStartIcon = createEventIcon("street_speeding_start");
export const StreetSpeedingEndIcon = createEventIcon("street_speeding_end");
export const RefuelIcon = createEventIcon("refuel");
export const MissingFuelIcon = createEventIcon("missing_fuel");
export const OverheatIcon = createEventIcon("overheat");

/** An event icon as an HTML string, for Leaflet markup that has no React in it. */
export function vehicleEventIconSvg(type: KnownVehicleEventType, size: number, color: string): string {
  return `<svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="${color}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" style="flex-shrink:0;">${VEHICLE_EVENT_ICON_BODIES[type]}</svg>`;
}
